<template>
  <view-page
    :title="roleLabel"
    :delete-dialog-title="$t('DeleteUser')"
    :delete-dialog-content="
      $t('DeleteConfirmation') + ' ' + $tc('Role', 1) + ' ' + roleLabel + '?'
    "
    permission-name="user"
    :edit-button="isAdmin || isSuperAdmin"
    :delete-button="canRemoveItems"
    @editItem="editRole"
    @deleteItem="deleteRole"
    :loading="loading"
  >
    <template v-slot:leftSide>
      <view-page-data-field
        v-for="field in fields"
        :key="field.label"
        :icon="field.icon"
        :label="field.label"
        :data="field.data"
        :hasPermission="field.hasPermission"
      />
    </template>

    <template v-slot:headerRightSide>
      <management-tabs-and-search
        :tabs="tabs"
        :open-tab="openTab"
        @update-open-tab="openTab = $event"
        :search="search"
        @update-search="search[$event.tab] = $event.value"
      />
    </template>

    <template v-slot:rightSide>
      <v-tabs-items :value="openTab" v-if="role">
        <v-tab-item value="permissions">
          <permissions-section
            account-type="clientRole"
            :previousRolesAndPermissions="previousRolesAndPermissions"
            type="view"
          />
        </v-tab-item>
        <v-tab-item value="webUsers">
          <associated-users
            action-type="view"
            user-type="web"
            from="role"
            :associated-users="role.usersWebAssociates"
            :search-from-parent="search.webUsers"
          />
        </v-tab-item>

        <v-tab-item value="mobileUsers">
          <associated-users
            action-type="view"
            user-type="mobile"
            from="role"
            :associated-users="role.usersMobileAssociates"
            :search-from-parent="search.mobileUsers"
          />
        </v-tab-item>
        <v-tab-item value="companies">
          <associated-companies
            from="role"
            action-type="view"
            :associated-companies="role.companies"
            :search-from-parent="search.companies"
          />
        </v-tab-item>
      </v-tabs-items>
    </template>
  </view-page>
</template>

<script>
import ViewPage from "@/components/Management/ViewPage/ViewPage";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import ViewPageDataField from "@/components/Management/ViewPage/ViewPageDataField";
import ManagementTabsAndSearch from "@/components/_Organisms/ManagementTabsAndSearch";
import PermissionsSection from "@/components/SystemManagement/Accounts/Permissions/Permissions";
import AssociatedUsers from "@/components/SystemManagement/Accounts/AssociatedUsers";
import AssociatedCompanies from "@/components/SystemManagement/Accounts/AssociatedCompanies";
import { mapActions } from "vuex";
import { clientRoleMixin } from "@/mixins/management/clientRoleMixin";

export default {
  name: "RoleView",
  components: {
    AssociatedCompanies,
    AssociatedUsers,
    PermissionsSection,
    ManagementTabsAndSearch,
    ViewPageDataField,
    ViewPage
  },

  mixins: [checkPermissionsMixin, clientRoleMixin],

  data() {
    return {
      /**
       * Determines the fields to be displayed on the left side of the page
       */
      fields: null,

      /**
       * Determines which tab is being displayed.
       */
      openTab: "permissions",

      /**
       * Saves the value of the respective search of each tab
       */
      search: {
        webUsers: "",
        mobileUsers: "",
        companies: ""
      },

      /**
       * Define if the page is loading
       */
      loading: true
    };
  },

  computed: {
    /**
     * Defines the tabs that appear on the left side
     * {String} value - That will be used to define which tab will be displayed
     * {String} text - Tab title
     * {Boolean} hasSearch - Defines if the tab will have the search bar
     */
    tabs() {
      return [
        {
          value: "permissions",
          text: this.$i18n.tc("Permission", 2)
        },
        {
          value: "webUsers",
          text: this.$i18n.tc("User", 2) + ": Web",
          hasSearch: true
        },
        {
          value: "mobileUsers",
          text: this.$i18n.tc("User", 2) + ": Mobile",
          hasSearch: true
        },
        {
          value: "companies",
          text: this.$i18n.tc("Company", 2),
          hasSearch: true
        }
      ];
    },

    /**
     * Determines the title of the page
     * @return {String}
     */
    roleLabel() {
      return this.role.label ? this.role.label : "";
    },

    /**
     * Determines whether the user can remove item
     * @return {boolean}
     */
    canRemoveItems() {
      if (this.role.blocked) {
        return (this.isAdmin || this.isSuperAdmin) && !this.role.blocked;
      }
    }
  },

  mounted() {
    this.fetchRoleDataByUuid().then(() => {
      this.fields = this.getFields();
    });
  },

  methods: {
    ...mapActions(["deleteClientRoleByUuid"]),

    /**
     * Define the values of the fields variable
     * {String} icon - Field icon
     * {String} label - Field label
     * {String} data - Field value
     * {Boolean} hasPermission - Determines if the user has permission to see the field.
     */
    getFields() {
      return [
        {
          icon: "subject",
          label: this.$i18n.t("Description"),
          data: this.role.description,
          hasPermission: true
        },
        {
          icon: "assignment_turned_in",
          label: this.$i18n.t("Status"),
          data: this.role.active
            ? this.$i18n.t("Active")
            : this.$i18n.t("Inactive"),
          hasPermission: true
        },
        {
          icon: "block",
          label: this.$i18n.t("Blocked"),
          data: this.role.blocked ? this.$i18n.t("Yes") : this.$i18n.t("No"),
          hasPermission: true
        },
        {
          icon: "mdi-eye",
          label: this.$i18n.t("Access"),
          data: this.role.visible
            ? this.$i18n.t("Visible")
            : this.$i18n.t("NotVisible"),
          hasPermission: true
        }
      ];
    },

    /**
     * Sends a request to delete the selected client role and and push it to the client roles page.
     */
    async deleteRole() {
      try {
        this.loading = true;
        await this.deleteClientRoleByUuid(this.role.uuid);

        /**
         * @property {String} name - Name of the route
         */
        await this.$router.push({ name: "client-roles" });
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Push to the page to edit the selected client role
     */
    editRole() {
      /**
       * @property {String} name - Name of the route
       * @property {Object} params - Parameters needed for the route path
       */
      this.$router.push({
        name: "client-role-edit",
        params: { uuid: this.role.uuid }
      });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/SystemManagement/Accounts/ClientRoles/ClientRoleView.vue